import { useEffect, useState, type FormEvent } from 'react'
import {
  PlayCircle, PauseCircle, XCircle,
  ArrowCounterClockwise as RotateCcw,
  type Icon as LucideIcon,
} from '@phosphor-icons/react'
import { Modal } from './Modal'
import { supabase } from '@/lib/supabase'
import type { EstadoAsociado } from '@/lib/typesNeoCore'

type Props = {
  open: boolean
  onClose: () => void
  asociadoId: string
  nombreCompleto: string
  estadoActual: EstadoAsociado
  onSuccess: () => void
}

type Transicion = {
  to: EstadoAsociado
  label: string
  description: string
  Icon: LucideIcon
  tone: 'success' | 'warn' | 'danger' | 'info'
  confirmKeyword?: string  // si exige typear ej. "RETIRAR" para confirmar
}

/**
 * Reglas de transición del negocio:
 *   prospecto  → activo (paga aportación)        | retirado (no completó proceso)
 *   activo     → suspendido (mora/AML/manual)    | retirado (baja definitiva)
 *   suspendido → activo (reactivación)            | retirado (baja definitiva)
 *   retirado   → (terminal, no transiciona)
 *
 * `pendiente` queda en el enum por compat pero NO se expone en UI.
 */
const TRANSICIONES: Record<EstadoAsociado, Transicion[]> = {
  prospecto: [
    // La activación viene automática desde el módulo Caja al registrar el
    // pago de cuota + aportación (NC-50d).
    {
      to: 'retirado',
      label: 'Descartar prospecto',
      description: 'Para cuando alguien inició el proceso pero no completó la membresía. Se archiva sin afectar movimientos.',
      Icon: XCircle,
      tone: 'danger',
    },
  ],
  activo: [
    {
      to: 'suspendido',
      label: 'Suspender temporalmente',
      description: 'Casos de mora, revisión AML o decisión administrativa. Se reactiva cuando se resuelva.',
      Icon: PauseCircle,
      tone: 'warn',
    },
    {
      to: 'retirado',
      label: 'Cerrar asociación',
      description: 'Baja definitiva por solicitud del asociado, fallecimiento o cierre administrativo. Requiere confirmación adicional.',
      Icon: XCircle,
      tone: 'danger',
      confirmKeyword: 'cerrar',
    },
  ],
  suspendido: [
    {
      to: 'activo',
      label: 'Reactivar',
      description: 'Restaurar acceso a operaciones.',
      Icon: RotateCcw,
      tone: 'success',
    },
    {
      to: 'retirado',
      label: 'Cerrar asociación',
      description: 'Baja definitiva. Requiere confirmación adicional.',
      Icon: XCircle,
      tone: 'danger',
      confirmKeyword: 'cerrar',
    },
  ],
  pendiente: [
    {
      to: 'activo',
      label: 'Activar',
      description: 'Pasa a ser asociado formal.',
      Icon: PlayCircle,
      tone: 'success',
    },
  ],
  retirado: [],
}

export function CambiarEstadoModal({
  open, onClose, asociadoId, nombreCompleto, estadoActual, onSuccess,
}: Props) {
  const [seleccion, setSeleccion] = useState<Transicion | null>(null)
  const [motivo, setMotivo] = useState('')
  const [confirmText, setConfirmText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const opciones = TRANSICIONES[estadoActual] ?? []

  useEffect(() => {
    if (!open) return
    setSeleccion(null)
    setMotivo('')
    setConfirmText('')
    setError(null)
  }, [open])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!seleccion) {
      setError('Seleccioná una acción.')
      return
    }
    if (!motivo.trim() || motivo.trim().length < 4) {
      setError('Indicá el motivo (queda en el histórico).')
      return
    }
    if (seleccion.confirmKeyword && confirmText.trim().toLowerCase() !== seleccion.confirmKeyword) {
      setError(`Escribí "${seleccion.confirmKeyword}" para confirmar.`)
      return
    }

    setSubmitting(true)
    const { error: rpcErr } = await supabase.rpc('cambiar_estado_asociado', {
      p_asociado_id: asociadoId,
      p_nuevo_estado: seleccion.to,
      p_motivo: motivo.trim(),
    })
    setSubmitting(false)

    if (rpcErr) {
      setError(`No se pudo guardar el cambio: ${rpcErr.message}`)
      return
    }

    onSuccess()
    onClose()
  }

  if (estadoActual === 'retirado') {
    return (
      <Modal open={open} onClose={onClose} title="Asociación cerrada" size="sm">
        <div className="empty-state" style={{ padding: 24 }}>
          <div className="empty-icon">
            <XCircle size={20} weight="duotone" />
          </div>
          <p className="empty-title">Asociación cerrada</p>
          <p style={{ margin: 0 }}>
            La baja fue definitiva. El histórico queda disponible para consulta.
          </p>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cambiar estado del asociado"
      subtitle={`${nombreCompleto} — estado actual: ${estadoActual}`}
      size="md"
      blocking={submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="submit"
            form="cambiar-estado-form"
            className="btn btn-primary"
            disabled={!seleccion || submitting}
          >
            {submitting ? 'Guardando…' : 'Aplicar cambio'}
          </button>
        </>
      }
    >
      <form id="cambiar-estado-form" onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

        <div>
          <p className="form-label" style={{ marginBottom: 10 }}>Acción a aplicar</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {opciones.map((op) => {
              const isSel = seleccion?.to === op.to
              return (
                <button
                  key={op.to}
                  type="button"
                  onClick={() => { setSeleccion(op); setConfirmText('') }}
                  className={`estado-option estado-option-${op.tone}` + (isSel ? ' selected' : '')}
                >
                  <div className="estado-option-icon">
                    <op.Icon size={18} weight="duotone" />
                  </div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div className="estado-option-label">{op.label}</div>
                    <div className="estado-option-desc">{op.description}</div>
                  </div>
                  <div className="estado-option-radio">
                    {isSel && <div className="estado-option-radio-dot" />}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        <div>
          <label className="form-label" htmlFor="motivo" style={{ marginBottom: 6, display: 'block' }}>
            Motivo
          </label>
          <textarea
            id="motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej. Solicitud del asociado vía WhatsApp el 29-abr."
            rows={3}
            style={{ resize: 'vertical', minHeight: 70 }}
          />
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Se registra en el histórico del asociado.
          </p>
        </div>

        {seleccion?.confirmKeyword && (
          <div>
            <label className="form-label" htmlFor="confirm" style={{ marginBottom: 6, display: 'block' }}>
              Confirmá escribiendo <code style={{ background: 'var(--bg)', padding: '1px 6px', borderRadius: 4, fontFamily: 'var(--font-mono)' }}>{seleccion.confirmKeyword}</code>
            </label>
            <input
              id="confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={seleccion.confirmKeyword}
            />
          </div>
        )}
      </form>
    </Modal>
  )
}
